import { useMemo } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/free-solid-svg-icons';

import { Link } from '~/components/global';
import useUser from '~/lib/user';
import useSumoDay from '~/lib/util/sumoDay';
import useExperiment from '~/lib/experiment';
import {
  ASKSUMO_CLEANUP_EXPERIMENT_NAME,
  PROJECT_FOMO_EXPERIMENT_NAME,
  NAV_FREE_DEALS_EXPERIMENT_NAME,
} from '~/lib/util/constants';

import CommentDotsGradient from '../../../public/comment-dots-gradient.svg';

const addQueryHelper = (href: string, query: Array<string>): string => {
  return query.length ? `${href}?${query.join('&')}` : href;
};

function AskSumoNavItem() {
  const router = useRouter();
  const { user } = useUser();
  const { isSumoDay } = useSumoDay();
  const { variant } = useExperiment(ASKSUMO_CLEANUP_EXPERIMENT_NAME);

  if (variant !== 'enabled') {
    return null;
  }

  return (
    <li
      key="asksumo"
      className="my-2 mr-8 md:my-0 md:flex md:h-full md:items-center"
    >
      <Link
        href="/asksumo/"
        className={clsx(
          'truncate font-header text-xl font-semibold',
          'md:font-sans md:text-base md:font-normal',
          'border-b-2 border-transparent pb-4 pt-3 leading-6 md:hover:border-blue-500',
          { 'md:border-blue-500': router.asPath.indexOf('/asksumo/') === 0 },
        )}
      >
        <Image
          src={CommentDotsGradient}
          width="25"
          alt=""
          role="presentation"
          className="mr-1.5 inline-block"
        />
        AskSumo
        {!user && !isSumoDay && <span>&nbsp;and get 10% off</span>}
      </Link>
    </li>
  );
}

export default function NavGroups() {
  const { isSumoDay } = useSumoDay();

  const { variant: fomoVariant } = useExperiment(PROJECT_FOMO_EXPERIMENT_NAME);
  const fomoEnabled = fomoVariant === 'enabled';

  const { user } = useUser();
  const { variant: freeDealsVariant } = useExperiment(
    NAV_FREE_DEALS_EXPERIMENT_NAME,
    !!user?.id,
  );
  const router = useRouter();
  const { asPath } = router;

  const showFreeDeals = !user?.id && freeDealsVariant === 'enabled';

  // Append the query param to the href.  By doing this when they click the link there is no delay
  // in updating the search.  If we didn't do this, the search will update after the page loads.
  const navItems = useMemo(() => {
    const urlQueryParams = router?.query?.query
      ? [`query=${router.query.query}`]
      : [];

    if (fomoEnabled) {
      return [
        {
          name: 'Ending soon',
          href: addQueryHelper('/software/', [
            'sort=ending',
            ...urlQueryParams,
          ]),
        },
        {
          name: 'New!',
          href: addQueryHelper('/software/', [
            'sort=latest',
            ...urlQueryParams,
          ]),
        },
        {
          name: 'Marketing & sales',
          href: addQueryHelper('/software/marketing-sales/', urlQueryParams),
        },
        { name: 'All deals', href: addQueryHelper('/browse/', urlQueryParams) },
      ];
    }

    return [
      { name: 'Software', href: addQueryHelper('/software/', urlQueryParams) },
      {
        name: 'Courses & learning',
        href: addQueryHelper('/courses-learning/', urlQueryParams),
      },
      {
        name: 'Templates',
        href: addQueryHelper('/templates/', urlQueryParams),
      },
      {
        name: 'Creative resources',
        href: addQueryHelper('/creative-resources/', urlQueryParams),
      },
      ...(showFreeDeals
        ? [
            {
              name: 'Free deals',
              href: addQueryHelper('/collections/free/', urlQueryParams),
            },
          ]
        : []),
      ...(isSumoDay
        ? [
            {
              name: 'Sumo Day deals',
              href: addQueryHelper(
                '/collections/sumo-day-2023/',
                urlQueryParams,
              ),
              icon: { src: faTag, className: 'mr-2 text-dollar' },
            },
          ]
        : []),
    ];
  }, [router, fomoEnabled, isSumoDay, showFreeDeals]);

  return (
    <div className="w-full grow md:w-auto">
      <ul className="flex flex-col md:h-full md:flex-row md:items-center">
        {navItems.map((item) => (
          <li
            key={item.href}
            className="my-2 mr-8 md:my-0 md:flex md:h-full md:shrink-0 md:items-center"
          >
            <Link
              key={item.href}
              href={item.href}
              className={clsx(
                'font-header text-xl font-semibold',
                'md:font-sans md:text-base md:font-normal',
                'border-b-2 border-transparent pb-4 pt-3 leading-6 md:hover:border-bolt',
                { 'md:border-bolt': asPath.indexOf(item.href) === 0 },
              )}
            >
              {item.icon && (
                <FontAwesomeIcon
                  icon={item.icon.src}
                  className={item.icon.className}
                />
              )}
              {item.name}
            </Link>
          </li>
        ))}

        <AskSumoNavItem />
      </ul>
    </div>
  );
}
